import { useState, useEffect, useCallback } from 'react';
import { X, Zap, ArrowRight, ChevronRight } from 'lucide-react';

/**
 * SmartKick Predictions house-ad — an LSVegas-style promo carousel shown at
 * the top of the Live Scores Matches page. Rotates a few creatives that use
 * real media from the predictions product, with a CTA into the 14-day free
 * trial (in-app on iOS via the predictions promo modal — Apple-safe).
 *
 * Dismissible: hidden for the rest of the session, reappears next launch.
 */
const DISMISS_KEY = 'sk_predads_dismissed_v1';

const SLIDES = [
  {
    eyebrow: 'SmartKick · Limited offer',
    headline: '14 days FREE — full VIP',
    sub: 'Every AI pick, bet builder & Banker of the Day. Cancel anytime.',
    cta: 'Start 14-day free trial',
    img: '/vip-14day-advert.jpg',
    cover: true,
  },
  {
    eyebrow: 'SmartKick · AI Predictions',
    headline: "Know who's going to win",
    sub: 'Daily AI picks with confidence scores across 15+ leagues.',
    cta: 'Start 14-day free trial',
    img: '/appstore-1-dashboard.png',
  },
  {
    eyebrow: 'VIP · Bet Builders',
    headline: 'Best odds + player props',
    sub: 'VIP odds comparison, BTTS, cards & anytime-scorer tips.',
    cta: 'Unlock VIP free',
    img: '/app-preview-vip.png',
  },
  {
    eyebrow: 'SmartKick · Daily 10',
    headline: "Today's 10 best bets",
    sub: '10 settled picks a day + a 100-pt bankroll tracker.',
    cta: 'Try it free for 14 days',
    img: '/appstore-1-dashboard.png',
  },
];

const ScoresPredictionsAd = ({ onCtaClick }) => {
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(DISMISS_KEY) === '1'; } catch { return false; }
  });
  const [idx, setIdx] = useState(0);

  // Auto-advance the carousel.
  useEffect(() => {
    if (dismissed) return undefined;
    const t = setInterval(() => setIdx((i) => (i + 1) % SLIDES.length), 5000);
    return () => clearInterval(t);
  }, [dismissed]);

  const dismiss = useCallback((e) => {
    e?.stopPropagation();
    try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch { /* ignore */ }
    setDismissed(true);
  }, []);

  if (dismissed) return null;
  const slide = SLIDES[idx];

  return (
    <div className="mx-3 mt-3" data-testid="scores-predictions-ad">
      <div
        role="button"
        tabIndex={0}
        onClick={() => onCtaClick?.()}
        onKeyDown={(e) => { if (e.key === 'Enter') onCtaClick?.(); }}
        className="relative overflow-hidden rounded-2xl border border-emerald-500/30 bg-gradient-to-br from-emerald-950/70 via-[var(--sk-surface)] to-[var(--sk-surface)] cursor-pointer"
      >
        {/* Glow accent */}
        <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-emerald-500/20 blur-3xl pointer-events-none" />

        {/* Dismiss */}
        <button
          onClick={dismiss}
          className="absolute top-2 right-2 z-10 w-7 h-7 rounded-full bg-black/40 hover:bg-black/60 flex items-center justify-center text-zinc-300"
          aria-label="Dismiss ad"
          data-testid="predads-dismiss"
        >
          <X className="w-3.5 h-3.5" />
        </button>

        {slide.cover ? (
          /* Hero creative — full-width banner (image already carries the copy) */
          <div className="relative">
            <img
              src={slide.img}
              alt="SmartKick — 14 days free full VIP access"
              className="w-full h-auto object-cover"
              loading="lazy"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
            <div className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-2 p-3 bg-gradient-to-t from-black/80 to-transparent">
              <span className="text-[11px] font-black uppercase tracking-[0.12em] text-emerald-300 truncate">{slide.eyebrow}</span>
              <span
                className="inline-flex items-center gap-1.5 shrink-0 px-3.5 py-2 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white text-[12px] font-black shadow-[0_0_18px_rgba(16,185,129,0.45)] transition-colors"
                data-testid="predads-cta"
              >
                {slide.cta} <ArrowRight className="w-3.5 h-3.5" />
              </span>
            </div>
          </div>
        ) : (
        <div className="relative flex items-stretch gap-3 p-3.5 pr-2">
          {/* Left: copy */}
          <div className="flex-1 min-w-0 flex flex-col">
            <div className="flex items-center gap-1.5 mb-1.5">
              <div className="w-5 h-5 rounded-md bg-emerald-500 flex items-center justify-center shrink-0">
                <Zap className="w-3 h-3 text-white" strokeWidth={2.5} />
              </div>
              <span className="text-[9px] font-black uppercase tracking-[0.12em] text-emerald-400 truncate">{slide.eyebrow}</span>
            </div>

            <h3 className="text-[18px] font-black text-white leading-tight">{slide.headline}</h3>
            <p className="text-[11px] text-zinc-400 leading-snug mt-1 flex-1">{slide.sub}</p>

            <span
              className="mt-2.5 inline-flex items-center gap-1.5 self-start px-3.5 py-2 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white text-[12px] font-black shadow-[0_0_18px_rgba(16,185,129,0.45)] transition-colors"
              data-testid="predads-cta"
            >
              {slide.cta} <ArrowRight className="w-3.5 h-3.5" />
            </span>
          </div>

          {/* Right: phone-framed prediction screenshot */}
          <div className="shrink-0 w-[88px] self-center">
            <div className="rounded-xl overflow-hidden border-2 border-white/15 bg-black shadow-lg aspect-[9/16]">
              <img
                src={slide.img}
                alt="SmartKick AI predictions"
                className="w-full h-full object-cover object-top"
                loading="lazy"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            </div>
          </div>
        </div>
        )}

        {/* Dots + responsible-gambling note */}
        <div className="relative flex items-center justify-between px-3.5 pb-2.5 -mt-1">
          <div className="flex items-center gap-1.5">
            {SLIDES.map((_, i) => (
              <button
                key={i}
                onClick={(e) => { e.stopPropagation(); setIdx(i); }}
                className={`h-1.5 rounded-full transition-all ${i === idx ? 'w-4 bg-emerald-400' : 'w-1.5 bg-white/25'}`}
                aria-label={`Slide ${i + 1}`}
                data-testid={`predads-dot-${i}`}
              />
            ))}
          </div>
          <span className="inline-flex items-center gap-1 text-[9px] font-bold text-zinc-500">
            18+ · Ad <ChevronRight className="w-3 h-3" />
          </span>
        </div>
      </div>
    </div>
  );
};

export default ScoresPredictionsAd;
